import { useEffect, useRef, useState, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, ImageIcon, Paperclip, RotateCcw } from 'lucide-react';
import {
  type ShowSupportTicketResponse,
  type SupportTicketAttachment,
  type SupportTicketMessage
} from '@bliks-io/contracts';
import {
  Badge,
  Bubble,
  BubbleContent,
  Button,
  Card,
  Message,
  MessageGroup,
  Page,
  PageContent,
  Skeleton
} from '@bliks-io/ui';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useAuthStore } from '@/stores/auth-store';
import { useSupportTicket } from '@/api/queries/useSupportTicket';
import { useDownloadSupportAttachment } from '@/api/mutations/useSupport';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { formatDateTime, formatFileSize } from '@/lib/formatters';
import { canReopenTicket, getTicketStatusBadgeVariant, isTicketClosed } from './ticketStatus';
import { AttachmentPreviewDialog } from './components/AttachmentPreviewDialog';
import { ReplyComposer } from './components/ReplyComposer';

// Raster formats only: an SVG is an image too, but one that can carry script.
const PREVIEWABLE_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

function isPreviewableImage(attachment: SupportTicketAttachment): boolean {
  return PREVIEWABLE_IMAGE_TYPES.has(attachment.mimeType.toLowerCase());
}

export function SupportTicketDetailPage({ ticketId }: { ticketId: number }) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const { data, isLoading, isError } = useSupportTicket(ticketId);
  useDocumentTitle('pageTitles.supportTickets');

  const ticket = data?.ticket;
  const [reopening, setReopening] = useState(false);
  const messageCount = data?.messages.length;
  const threadEndRef = useRef<HTMLDivElement>(null);
  // Who reads further up keeps their place when a polled message arrives;
  // whoever is at the bottom, or just replied, follows the thread.
  const followsThreadRef = useRef(true);
  const followThreadIfAtBottom = (event: UIEvent<HTMLDivElement>) => {
    const box = event.currentTarget;
    followsThreadRef.current = box.scrollHeight - box.scrollTop - box.clientHeight < 80;
  };

  useEffect(
    function showLatestMessage() {
      if (messageCount !== undefined && followsThreadRef.current) {
        threadEndRef.current?.scrollIntoView({ block: 'end' });
      }
    },
    [messageCount]
  );

  // Static keys (not a dynamic t(`...${status}`) template) so check:translations
  // can resolve every status label; the keys mirror SupportTicketStatusSchema.
  const statusLabels = {
    received: t('supportTickets.status.received'),
    in_progress: t('supportTickets.status.inProgress'),
    resolved: t('supportTickets.status.resolved'),
    merged: t('supportTickets.status.merged')
  };

  return (
    <Page>
      {/* A chat window: the page stays put, only the thread scrolls between
          the one-line header and the reply composer. */}
      <PageContent className="flex flex-col overflow-hidden">
        <div className="mb-4 flex shrink-0 items-center gap-3">
          <Link
            to="/$slug/help/tickets"
            params={{ slug }}
            className="text-muted-foreground hover:text-foreground"
            aria-label={t('common.back')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="min-w-0 truncate text-xl font-bold tracking-tight">
            {ticket ? ticket.title : t('supportTickets.detail.title')}
          </h1>
          {ticket && (
            <>
              <span className="shrink-0 text-sm text-muted-foreground">#{ticket.number}</span>
              <Badge variant={getTicketStatusBadgeVariant(ticket.status)}>
                {statusLabels[ticket.status]}
              </Badge>
            </>
          )}
        </div>

        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-20 w-3/4" />
            <Skeleton className="h-20 w-3/4 ml-auto" />
          </div>
        )}

        {isError && (
          <Card className="p-8 text-center border-dashed">
            <AlertTriangle className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground font-medium mb-1">
              {t('supportTickets.detail.loadErrorTitle')}
            </p>
            <p className="text-sm text-muted-foreground">{t('supportTickets.detail.loadError')}</p>
          </Card>
        )}

        {data && (
          <>
            <div
              onScroll={followThreadIfAtBottom}
              className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-line bg-surface-subtle p-4"
            >
              <Conversation ticketId={ticketId} messages={data.messages} />
              <ThreadEnd ticket={data} reopening={reopening} onReopen={() => setReopening(true)} />
              <div ref={threadEndRef} />
            </div>
            {(!isTicketClosed(data.ticket.status) || reopening) && (
              <div className="shrink-0">
                <ReplyComposer
                  ticketId={ticketId}
                  reopens={canReopenTicket(data.ticket.status)}
                  onSent={() => {
                    setReopening(false);
                    followsThreadRef.current = true;
                  }}
                />
              </div>
            )}
          </>
        )}
      </PageContent>
    </Page>
  );
}

function MessageAttachments({
  ticketId,
  message
}: {
  ticketId: number;
  message: SupportTicketMessage;
}) {
  const { t } = useTranslation();
  const download = useDownloadSupportAttachment();
  const [previewed, setPreviewed] = useState<SupportTicketAttachment | null>(null);

  if (message.attachments.length === 0) return null;

  const downloadAttachment = (attachment: SupportTicketAttachment) =>
    download.mutate(
      { ticketId, messageId: message.id, attachment },
      {
        onError: (error) => toastApiError(error, 'supportTickets.detail.attachmentError')
      }
    );

  return (
    <>
      <ul className="mt-2 space-y-1">
        {message.attachments.map((attachment) => {
          const previewable = isPreviewableImage(attachment);
          return (
            <li key={attachment.id}>
              {/* A button, not an <a href>: the API authenticates with a bearer
                  header that a plain navigation would not carry, so the bytes are
                  fetched through the client and handed to the browser as a blob. */}
              <button
                type="button"
                disabled={!previewable && download.isPending}
                onClick={() =>
                  previewable ? setPreviewed(attachment) : downloadAttachment(attachment)
                }
                className="flex items-center gap-2 text-sm underline underline-offset-2 hover:no-underline disabled:opacity-50"
                aria-label={t(
                  previewable
                    ? 'supportTickets.detail.attachmentOpen'
                    : 'supportTickets.detail.attachmentDownload',
                  { name: attachment.filename }
                )}
              >
                {previewable ? (
                  <ImageIcon className="h-3.5 w-3.5 shrink-0" />
                ) : (
                  <Paperclip className="h-3.5 w-3.5 shrink-0" />
                )}
                <span className="truncate">{attachment.filename}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatFileSize(attachment.sizeBytes)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <AttachmentPreviewDialog
        ticketId={ticketId}
        messageId={message.id}
        attachment={previewed}
        onDownload={downloadAttachment}
        onClose={() => setPreviewed(null)}
      />
    </>
  );
}

function Conversation({
  ticketId,
  messages
}: {
  ticketId: number;
  messages: SupportTicketMessage[];
}) {
  const { t, i18n } = useTranslation();

  if (messages.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        {t('supportTickets.detail.noMessages')}
      </p>
    );
  }

  return (
    <MessageGroup className="gap-4">
      {messages.map((message) => {
        const fromCustomer = message.author === 'customer';
        return (
          <Message key={message.id} align={fromCustomer ? 'end' : 'start'}>
            <Bubble variant={fromCustomer ? 'accent' : 'muted'} className="max-w-3/4">
              <BubbleContent>
                {/* Plain text from the API — never markup. See articleBodyToText. */}
                {message.body && <p className="whitespace-pre-wrap break-words">{message.body}</p>}
                <MessageAttachments ticketId={ticketId} message={message} />
              </BubbleContent>
              <span className="px-1 text-xs text-muted-foreground">
                {fromCustomer
                  ? t('supportTickets.detail.authorYou')
                  : t('supportTickets.detail.authorSupport')}
                {' · '}
                {formatDateTime(message.createdAt, i18n.language)}
              </span>
            </Bubble>
          </Message>
        );
      })}
    </MessageGroup>
  );
}

/**
 * How a closed thread ends, as a line in the thread itself (like "… left the
 * chat" in a messenger) rather than a box under it: the thread keeps the
 * height the reply box no longer needs.
 */
function ThreadEnd({
  ticket,
  reopening,
  onReopen
}: {
  ticket: ShowSupportTicketResponse;
  reopening: boolean;
  onReopen: () => void;
}) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const { status } = ticket.ticket;

  if (status === 'merged') {
    // A merged ticket takes no reply: it carries on in the request it went
    // into, linked only when that one is the customer's own.
    return (
      <div className="mt-4 flex flex-col items-center gap-1 rounded-lg border border-warning-line bg-warning-surface px-4 py-3 text-center text-sm text-warning">
        <span>{t('supportTickets.detail.mergedNotice')}</span>
        {ticket.mergedInto && (
          <Link
            to="/$slug/help/tickets/$ticketId"
            params={{ slug, ticketId: ticket.mergedInto.id }}
            className="font-medium underline underline-offset-2 hover:no-underline"
          >
            {t('supportTickets.detail.mergedLink', { number: ticket.mergedInto.number })}
          </Link>
        )}
      </div>
    );
  }

  if (!isTicketClosed(status) || reopening) return null;

  // A closed ticket opens again with a message, the way a reply by mail
  // reopens it in Zammad: support then knows what is still open.
  return (
    <div className="mt-4 flex flex-col items-center gap-2 text-center text-sm text-muted-foreground">
      <span>{t('supportTickets.detail.resolvedHint')}</span>
      <Button variant="outline" size="small" onClick={onReopen}>
        <RotateCcw className="h-4 w-4 mr-1" />
        {t('supportTickets.detail.reopenAction')}
      </Button>
    </div>
  );
}
